import React, { useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { spacing, Text } from '@retailer-platform/shared-components'
import { IconButton } from '@instacart/ids-customers'
import { ArrowUpIcon, ArrowDownIcon } from '@instacart/ids-core'
import { type Mutation } from '../RegexForm'
import { useDomainMessages } from '../../../../utils/domain/intl'
import { RegexCard } from './RegexCard'

interface Props {
  mutations: Mutation[]
  readOnly: boolean
  onChange: (mutations: Mutation[]) => void
}

const RegexGrid = styled.div({
  width: '100%',
  padding: spacing.X16,
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  columnGap: spacing.X16,
  rowGap: spacing.X8,
  alignItems: 'center',
  justifyContent: 'center',
})

const OrderButtons = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X8,
  alignItems: 'center',
  justifyContent: 'center',
})

export const RegexCardList = ({ mutations, readOnly, onChange }: Props) => {
  const i18n = useDomainMessages({
    moveUp: 'catalogAdminDomain.attributeManagement.regex.form.mutation.moveUp',
    moveDown: 'catalogAdminDomain.attributeManagement.regex.form.mutation.moveDown',
  })
  const onOrderChange = useCallback(
    (index: number, change: number) => {
      const mutation = mutations[index]
      mutations[index] = mutations[index + change]
      mutations[index + change] = mutation
      onChange(mutations)
    },
    [mutations, onChange]
  )
  return (
    <RegexGrid>
      {mutations.map((mutation, index) => (
        <>
          <OrderButtons>
            <IconButton
              icon={ArrowUpIcon}
              onPress={() => onOrderChange(index, -1)}
              accessibleLabel={i18n.moveUp}
              disabled={readOnly || index === 0}
            />
            <Text weight="semibold">{index + 1}</Text>
            <IconButton
              icon={ArrowDownIcon}
              onPress={() => onOrderChange(index, +1)}
              accessibleLabel={i18n.moveDown}
              disabled={readOnly || index === mutations.length - 1}
            />
          </OrderButtons>
          <RegexCard
            key={mutation.inputField}
            mutation={mutation}
            readOnly={readOnly}
            onDelete={() => onChange(mutations.filter((_, i) => i !== index))}
            onChange={mutation => onChange(mutations.map((m, i) => (i === index ? mutation : m)))}
            isNew={false}
            bannedCombinations={mutations
              .filter(m => m.inputField !== mutation.inputField)
              .map(m => ({ inputField: m.inputField, outputField: m.outputField }))}
          />
        </>
      ))}
    </RegexGrid>
  )
}
